import React, { useState, useEffect } from "react";
import Web3 from "web3";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import {
    createDSA,
    createMakerVault,
    createGelatoOptimizer,
} from "gelato-instadapp-debt-bridge";
import { ethers } from "ethers";

import { daiABI } from "./abis/dai";
import RobotoRegularFont from "./fonts/Roboto/Roboto-Regular.ttf";
import RobotoLightFont from "./fonts/Roboto/Roboto-Light.ttf";
import { Table } from "./components/Table";
import { texts } from "./locales/en";

const DAI_TOKEN_ADDRESS = "0x6b175474e89094c44da98b954eedeac495271d0f";
const ETH_10 = makeEthBigNumberFromString("10");
const DAI_150 = makeDaiBigNumberFromString("150");

function makeEthBigNumberFromString(amount: string) {
    return ethers.utils.parseEther(amount);
}

function makeDaiBigNumberFromString(amount: string) {
    return ethers.utils.parseUnits(amount, 18);
}

export {};

declare global {
    interface Window {
        ethereum: any;
        web3: Web3;
    }
}

const GlobalStyles = createGlobalStyle`
    @font-face {
        font-family: 'Roboto';
        src: url(${RobotoLightFont}) format('truetype');
        font-weight: 300;
        font-style: normal;
    }
    @font-face {
        font-family: 'Roboto';
        src: url(${RobotoRegularFont}) format('truetype');
        font-weight: 400;
        font-style: normal;
    }
`;

const isEthEnabled = () => {
    if (window.ethereum) {
        window.web3 = new Web3(window.ethereum);
        window.ethereum.enable();
        return true;
    }
    return false;
};

async function getAccountInfo(
    setAccounts: any,
    setEthBalance: any,
    setDaiBalance: any
) {
    const accounts = await window.web3.eth.getAccounts();
    setAccounts(accounts);
    const ethBalance = (
        Number(await window.web3.eth.getBalance(accounts[0])) /
        10 ** 18
    ).toFixed(2);
    setEthBalance(ethBalance);
    const contract = new window.web3.eth.Contract(daiABI, DAI_TOKEN_ADDRESS);
    const daiBalance = Number(
        await contract.methods.balanceOf(accounts[0]).call()
    );
    setDaiBalance(daiBalance.toFixed());
    return { accounts, ethBalance, daiBalance };
}

function App() {
    const [accounts, setAccounts] = useState<string[] | undefined>(undefined);
    const [ethBalance, setEthBalance] = useState<string | undefined>(undefined);
    const [daiBalance, setDaiBalance] = useState<string | undefined>(undefined);
    const [dsaAddress, setDsaAddress] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!accounts) {
            getAccountInfo(setAccounts, setEthBalance, setDaiBalance)
                .then(({ accounts, ethBalance, daiBalance }) => {
                    console.log(
                        `Account number : ${accounts}, ethBalance: ${ethBalance},  daiBalance: ${daiBalance}`
                    );
                })
                .catch(console.error);
        }
    }, []);

    if (!isEthEnabled()) {
        alert("Install Metamask");
    }
    return (
        <MainContainer>
            <GlobalStyles />
            <HeaderRow>
                <UserAddressHeader>
                    <p>{texts.app.headers.userAddress(accounts)}</p>
                </UserAddressHeader>
                <UserBalanceHeader>
                    <p>
                        {texts.app.headers.userBalance(ethBalance, daiBalance)}
                    </p>
                </UserBalanceHeader>
            </HeaderRow>
            <VaultInfo>{texts.app.title("0.00", "XXXXX", "0")}</VaultInfo>
            <TableDiv>
                <Table
                    tableHeader={texts.app.table.defaultHeader}
                    tableRows={texts.app.table.defaultRows}
                />
            </TableDiv>
            <ButtonsDiv>
                <CreateDSAButton
                    onClick={() => createDsaClickHandler(setDsaAddress)}
                >
                    {texts.app.buttons.createDsa}
                </CreateDSAButton>
                <CreateMakerVaultButton
                    onClick={() =>
                        createMakerVaultClickHandler(dsaAddress, setDsaAddress)
                    }
                >
                    {texts.app.buttons.createMakerVault}
                </CreateMakerVaultButton>
                <SetUpGelato
                    onClick={() => {
                        setupGelatoClickHandler(dsaAddress);
                    }}
                >
                    {texts.app.buttons.setupGelato}
                </SetUpGelato>
                <SetUpGelato onClick={() => {}}>
                    Create Gelato Auto Liquidator
                </SetUpGelato>
                <SetUpGelato onClick={() => {}}>
                    Create Gelato Aave Optimizer
                </SetUpGelato>
            </ButtonsDiv>
            {dsaAddress ? <p>Your DSA address is : {dsaAddress}</p> : <div />}
        </MainContainer>
    );
}

function createDsaClickHandler(setDsaAddress: (address: string) => void) {
    return createDSA(window.web3 as any)
        .then((address: string) => {
            setDsaAddress(address);
        })
        .catch(console.error);
}

function createMakerVaultClickHandler(
    dsaAddress: string | undefined,
    setDsaAddress: (dsaAddress: string) => void
) {
    let customDsaAddress = undefined;
    if (!dsaAddress) {
        customDsaAddress = prompt("Enter DSA address");
        if (customDsaAddress) {
            setDsaAddress(customDsaAddress);
        } else {
            throw new Error("No DSA address entered");
        }
    }
    const address = dsaAddress || customDsaAddress;
    if (address) {
        const ethAmount = prompt("Enter ETH amount (default 10)");
        const daiAmount = prompt("Enter DAI amount (default 150)");
        return createMakerVault(
            window.web3 as any,
            address,
            ethAmount ? makeEthBigNumberFromString(ethAmount) : ETH_10,
            daiAmount ? makeDaiBigNumberFromString(daiAmount) : DAI_150
        )
            .then(() => {
                alert("Vault creation succeeded !");
            })
            .catch((err) => {
                alert("Vault creation failed");
                console.error(err);
            });
    } else {
        alert("No DSA address entered");
    }
}
function setupGelatoClickHandler(dsaAddress?: string) {
    if (!dsaAddress) {
        alert("No DSA address");
    } else {
        const mockCDAIAddress = prompt("Enter mockCDAIAddress");
        const mockDSRAddress = prompt("Enter mockDSRAddress");
        const conditionAddress = prompt("Enter conditionAddress");
        const conditionHasMakerVaultAddress = prompt(
            "Enter conditionHasMakerVaultAddress"
        );
        if (
            mockCDAIAddress &&
            mockDSRAddress &&
            conditionAddress &&
            conditionHasMakerVaultAddress
        ) {
            const ethAmount = prompt("Enter ETH amount (default 10)");
            const daiAmount = prompt("Enter DAI amount (default 150)");
            return createGelatoOptimizer(
                window.web3,
                dsaAddress,
                ethAmount ? makeEthBigNumberFromString(ethAmount) : ETH_10,
                daiAmount ? makeDaiBigNumberFromString(daiAmount) : DAI_150,
                mockCDAIAddress,
                mockDSRAddress,
                conditionAddress,
                conditionHasMakerVaultAddress
            )
                .then(() => {
                    alert("Gelato contract setup succeded !");
                })
                .catch((err: Error) => {
                    alert("Gelato contract setup failed !");
                    console.error(err);
                });
        } else {
            alert("Missing parameters");
        }
    }
}

const MainContainer = styled.div`
    text-align: center;
    font-family: "Roboto";
    height: 100vh;
    background-image: url(${require("./images/sky2.jpg")});
    background-repeat: no-repeat;
    background-size: cover;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
`;

const UserAddressHeaderKeyframes = keyframes`
    from {
        opacity: 0;
        color: #da46ff;
    }
    to {
        opacity: 1;
        color: white;
    }
`;

const UserAddressHeader = styled.header`
    border: 3px;
    height: 4vw;
    width: 50vw;
    font-size: 1.3vw;
    font-weight: bold;
    text-shadow: 1px 1px rgba(0, 0, 0, 0.4);
    color: white;
    position: fixed;
    top: 0;
    left: 0;
    border: 3px solid;
    border-radius: 10px;
    opacity: 1;
    animation-name: ${UserAddressHeaderKeyframes};
    animation-duration: 3s;
    box-shadow: 0px 3px 3px rgba(0, 0, 0, 0.4);
    background-color: #3a90da;
`;

const UserBalanceHeaderKeyframes = keyframes`
    from {
        opacity: 0;
        color: #da46ff;
    }
    to {
        opacity: 1;
        color: white;
    }
`;

const UserBalanceHeader = styled.header`
    height: 4vw;
    width: 30vw;
    font-size: 1.3vw;
    font-weight: bold;
    text-shadow: 1px 1px rgba(0, 0, 0, 0.4);
    color: white;
    position: fixed;
    top: 0;
    right: 0;
    border: 3px solid;
    border-radius: 10px;
    opacity: 1;
    animation-name: ${UserBalanceHeaderKeyframes};
    animation-duration: 3s;
    box-shadow: 0px 3px 3px rgba(0, 0, 0, 0.4);
    background-color: #3a90da;
`;

const HeaderRow = styled.div`
    display: flex;
    flex-direction: row;
`;

const VaultInfoKeyframes = keyframes`
    from {
        opacity: 0;
        color: #ffffff;
    }
    to {
        opacity: 1;
        color: #f5f1ed;
    }
`;

const VaultInfo = styled.div`
    font-size: 2vw;
    font-weight: 400;
    border: solid 3px;
    margin-top: 10vw;
    margin-bottom: 3vw;
    margin-left: 20vw;
    margin-right: 20vw;
    color: #f5f1ed;
    animation-name: ${VaultInfoKeyframes};
    animation-duration: 2s;
    text-shadow: 1px 1px rgba(0, 0, 0, 0.4);
    box-shadow: 0px 3px 3px rgba(0, 0, 0, 0.4);
`;

const TableDiv = styled.div`
    font-size: 1.6vw;
    width: 75%;
    margin: 4vw auto;
`;

const ButtonsDiv = styled.div`
    margin-left: 20vw;
    margin-right: 20vw;
    position: relative;
    display: flex;
    border-spacing: 20px;
    flex-direction: row;
    justify-content: space-between;
`;

const CreateMakerVaultButton = styled.button`
    font-family: "Roboto";
    font-size: 1.2vw;
    font-weight: bold;
    color: #f5f1ed;
    background-color: #3a90da;
    width: 10vw;
    height: 5vw;
    border-radius: 10px;
    border: 2px solid white;
    box-shadow: 0px 3px 3px rgba(0, 0, 0, 0.4);
`;

const CreateDSAButton = styled.button`
    font-family: "Roboto";
    font-size: 1.2vw;
    font-weight: bold;
    color: #f5f1ed;
    background-color: blue;
    width: 10vw;
    height: 5vw;
    border-radius: 10px;
    border: 2px solid white;
    box-shadow: 0px 3px 3px rgba(0, 0, 0, 0.4);
`;

const SetUpGelato = styled.button`
    font-family: "Roboto";
    font-size: 1.2vw;
    font-weight: bold;
    color: white;
    background-color: #2c67bf;
    width: 10vw;
    height: 5vw;
    border-radius: 10px;
    border: 2px solid white;
    box-shadow: 0px 3px 3px rgba(0, 0, 0, 0.4);
`;

export default App;
